import React, { useState, useEffect, useRef } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectPayments,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import { BASE_URL, STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface StripeConnectManagerProps {
  onComplete?: () => void;
  /**
   * 'full' (default) keeps the two action buttons.
   * 'onboarding' / 'payments' auto-load the matching Stripe component without buttons.
   */
  mode?: 'full' | 'onboarding' | 'payments';
}

const StripeConnectManager: React.FC<StripeConnectManagerProps> = ({ onComplete, mode = 'full' }) => {
  const { token, refreshUserData } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'configurer' | 'payment-details' | null>(
    mode === 'payments' ? 'payment-details' : mode === 'onboarding' ? 'configurer' : null
  );
  const [stripeInstance, setStripeInstance] = useState<any>(null);
  const autoStarted = useRef(false);

  const createProStripeAccount = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/create-stripe-bank-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erreur inconnue');
      }
      return data.account;
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
      return undefined;
    }
  };

  const fetchClientSecret = async (account: string) => {
    try {
      const response = await fetch(`${BASE_URL}/api/create-account-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ account }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erreur client_secret');
      }
      return data.client_secret;
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
      return undefined;
    }
  };

  const initializeStripe = async () => {
    setIsLoading(true);
    try {
      const account = await createProStripeAccount();
      if (!account) {
        setIsLoading(false);
        return;
      }
      const instance = await loadConnectAndInitialize({
        publishableKey: STRIPE_PUBLISHABLE_KEY,
        fetchClientSecret: () => fetchClientSecret(account),
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: '#ffffff',
          },
        },
      });
      setStripeInstance(instance);
    } catch (error) {
      console.error('Error during Stripe initialization:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'initialisation de Stripe.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (mode !== 'full' && !autoStarted.current) {
      autoStarted.current = true;
      initializeStripe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleConfigureStripe = async () => {
    await initializeStripe();
    setSelectedAction('configurer');
  };

  const handleViewPayments = async () => {
    await initializeStripe();
    setSelectedAction('payment-details');
  };

  return (
    <div className="space-y-4 w-full">
      {mode === 'full' && (
        <div className="flex space-x-4">
          <Button
            variant="outline"
            onClick={handleConfigureStripe}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t('invoices.settings.paymentMethods.stripeSettingNow')}
          </Button>
          <Button
            variant="outline"
            onClick={handleViewPayments}
            disabled={isLoading}
            className="w-full"
          >
            {t('shop.showPayments')}
          </Button>
        </div>
      )}
      {mode !== 'full' && isLoading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      <div id="embedded-onboarding-container" className="w-full rounded-lg p-3">
        {stripeInstance && (
          <ConnectComponentsProvider connectInstance={stripeInstance}>
            {selectedAction === 'payment-details' ? (
              <ConnectPayments />
            ) : (
              <ConnectAccountOnboarding
                onExit={async () => {
                  setStripeInstance(null);
                  await refreshUserData();
                  onComplete?.();
                }}
              />
            )}
          </ConnectComponentsProvider>
        )}
      </div>
    </div>
  );
};

export default StripeConnectManager;
